/* poser-crt.js -- CRT/fisheye barrel distortion for the homepage video
   background. Draws the looping <video> into a WebGL texture and warps it
   in a fragment shader. Replaces the SVG feDisplacementMap approach, which
   is broken on iOS/WebKit Safari (WebKit bugs #211617 / #104169).

   DOM contract (inside .poser-entry):
     <video  class="poser-entry__bg-src" data-curve data-speed>  -- source
     <canvas class="poser-entry__bg">                            -- output

   Progressive enhancement: the <video> plays and is visible on its own;
   once the shader produces its first frame the raw video is hidden and the
   curved canvas takes over. No WebGL / reduced-motion -> graceful fallback. */
(function () {
  'use strict';

  var VERT = 'attribute vec2 aPos;varying vec2 vUv;' + 'void main(){vUv=aPos*0.5+0.5;gl_Position=vec4(aPos,0.0,1.0);}';

  var FRAG =
    'precision mediump float;varying vec2 vUv;' +
    'uniform sampler2D uTex;uniform vec2 uVideoRes;uniform vec2 uCanvasRes;' +
    'uniform float uCurve;' +
    'void main(){' +
    '  float va=uVideoRes.x/uVideoRes.y;' +
    '  float ca=uCanvasRes.x/uCanvasRes.y;' +
    '  vec2 sc=vec2(1.0);' +
    '  if(ca>va){sc.y=va/ca;}else{sc.x=ca/va;}' /* cover-fit */ +
    '  vec2 uv=(vUv-0.5)*sc+0.5;' +
    '  uv=(uv-0.5)*0.90+0.5;' /* minimal fixed overscan zoom */ +
    '  vec2 cc=uv-0.5;float r2=dot(cc,cc);' +
    '  uv+=cc*r2*uCurve;' /* barrel / fisheye push-out */ +
    '  float amt=0.015*r2;' /* subtle CRT chromatic aberration */ +
    '  float r=texture2D(uTex,uv+cc*amt).r;' +
    '  float g=texture2D(uTex,uv).g;' +
    '  float b=texture2D(uTex,uv-cc*amt).b;' +
    '  gl_FragColor=vec4(r,g,b,1.0);' +
    '}';

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      return null;
    }
    return s;
  }

  function initCRT(root) {
    var video = root.querySelector('.poser-entry__bg-src') || root.querySelector('.poser-product__bg-src') || root.querySelector('.poser-cart__bg-src') || root.querySelector('.poser-collection__bg-src');
    var canvas = root.querySelector('.poser-entry__bg') || root.querySelector('.poser-product__bg') || root.querySelector('.poser-cart__bg') || root.querySelector('.poser-collection__bg');
    if (!video || !canvas) {
      return;
    }

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      try {
        video.pause();
      } catch (e) {}
      return;
    }

    // honour the playback-speed setting
    var speed = (parseFloat(video.dataset.speed) || 100) / 100;
    var applySpeed = function () {
      video.playbackRate = speed;
    };
    applySpeed();
    video.addEventListener('loadedmetadata', applySpeed);
    video.addEventListener('play', applySpeed);

    var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      video.style.opacity = '1';
      canvas.style.display = 'none';
      return;
    }

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) {
      video.style.opacity = '1';
      canvas.style.display = 'none';
      return;
    }
    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      video.style.opacity = '1';
      canvas.style.display = 'none';
      return;
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    var aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    var uTex = gl.getUniformLocation(prog, 'uTex');
    var uVideoRes = gl.getUniformLocation(prog, 'uVideoRes');
    var uCanvasRes = gl.getUniformLocation(prog, 'uCanvasRes');
    var uCurve = gl.getUniformLocation(prog, 'uCurve');
    var curve = (parseFloat(video.dataset.curve) || 0) / 400; // px slider -> shader k

    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.uniform1i(uTex, 0);
    gl.uniform1f(uCurve, curve);

    function resize() {
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      var h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
      gl.uniform2f(uCanvasRes, canvas.width, canvas.height);
    }
    window.addEventListener('resize', resize);

    var shown = false,
      raf;
    function render() {
      if (video.readyState >= 2 && video.videoWidth && canvas.clientWidth > 2) {
        resize();
        gl.uniform2f(uVideoRes, video.videoWidth, video.videoHeight);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        try {
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, video);
          gl.drawArrays(gl.TRIANGLES, 0, 6);
          if (!shown) {
            shown = true;
            video.style.opacity = '0';
          }
        } catch (e) {
          /* texture not ready yet */
        }
      }
      raf = requestAnimationFrame(render);
    }

    var playing = video.play();
    if (playing && playing.catch) {
      playing.catch(function () {});
    }
    render();
  }

  function boot() {
    var roots = document.querySelectorAll('.poser-entry, .poser-product, .poser-cart, .poser-about, .poser-collection');
    for (var i = 0; i < roots.length; i++) {
      initCRT(roots[i]);
    }
  }
  if (document.readyState !== 'loading') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
  }
})();
